import { AppBar, Toolbar, Box, Avatar, Menu, MenuItem, Button as MUIButton, ListItemIcon } from '@mui/material';
import { useState, useEffect } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsLoggedIn, selectUserInfo, setLogoutState } from '../../config/UserSlice';
import * as apiUtility from '../../config/APIUtils';
import ltcLogo from "./CARE-LTC-logo.png";
import passLogo from "./carepassport logo.png";
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import { Typography } from '@mui/joy';
import { styled } from '@mui/material/styles';
import Badge from '@mui/material/Badge';

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#44b700',
    color: '#44b700',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));

function Header() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { home } = useParams() || {};
  const currentHome = home || '';

  // Get login state from Redux
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const userInfo = useSelector(selectUserInfo);
  const fullName = userInfo?.fullName || sessionStorage.getItem("fullName") || "";
  const userName = userInfo?.userName || sessionStorage.getItem("userName") || "";
  const userId = userInfo?.userId || sessionStorage.getItem("userId") || "";

  // Determine dashboard URL based on user type
  const getDashboardUrl = () => {
    const userType = userInfo?.userType || sessionStorage.getItem('userType');
    if (userType === "admin") {
      return currentHome ? `/${currentHome}/admin/` : `/admin/`;
    }
    return currentHome ? `/${currentHome}/user/${userId}` : `/user/${userId}`;
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAvatarMouseEnter = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDashboard = () => {
    handleMenuClose();
    navigate(getDashboardUrl());
  };

  const handleLogout = async () => {
    handleMenuClose();
    await apiUtility.logOut();
    dispatch(setLogoutState());
    navigate((currentHome ? `/${currentHome}/login` : `/login`));
  };

  const handleLogin = () => {
    navigate((currentHome ? `/${currentHome}/login` : `/login`));
  };

  // Function to generate color from string
  const stringToColor = (string) => {
    let hash = 0;
    let i;

    for (i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = '#';
    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
  };

  // Function to get avatar initials
  const getAvatarInitials = (name) => {
    if (!name || name.length === 0) return "U";
    const parts = name.split(' ');
    if (parts.length > 1) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return parts[0][0].toUpperCase();
  };

  const ltcLogoStyle = {
    width: isMobile ? '100px' : '140px',
    height: isMobile ? '60px' : '90px',
    marginRight: isMobile ? '20px' : '40px',
    cursor: 'pointer'
  };

  const passLogoStyle = {
    width: isMobile ? '180px' : '300px',
    height: isMobile ? '30px' : '45px',
    marginRight: isMobile ? '5px' : '1.5pc',
    cursor: 'pointer'
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: '#ffffff',
        color: '#333',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        zIndex: 1049,
      }}
    >
      <Toolbar sx={{
        paddingX: { xs: '1rem', sm: '1rem' },
        paddingY: { xs: '0.3rem', sm: '0.35rem' }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <img
            src={ltcLogo}
            alt="Care-ltc-logo"
            style={ltcLogoStyle}
            onClick={() => window.open("https://care-ltc.ca/", '_blank')}
          />
          <img
            src={passLogo}
            alt="Care-passport-logo"
            style={passLogoStyle}
            onClick={() => (currentHome ? navigate(`/${currentHome}/`) : navigate(`/`))}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 3 } }}>
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 2 }}>
            <MUIButton
              color="inherit"
              onClick={() => navigate(`/aboutUs`)}
              sx={{
                fontSize: '1.0rem',
                textTransform: 'none',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' }
              }}
            >
              About Us

            </MUIButton>
            <MUIButton
              color="inherit"
              onClick={() => navigate(`/contactUs`)}
              sx={{
                fontSize: '1.0rem',
                textTransform: 'none',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' }
              }}
            >
              Contact Us
            </MUIButton>
            <MUIButton
              color="inherit"
              href="https://carepassportcareltc.atlassian.net/wiki/external/ODA0NDdkYjllNTVjNDhjZThlNGRiNDE5NGU1MGZiZmY"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontSize: '1.0rem',
                textTransform: 'none',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' }
              }}
            >
              Help
            </MUIButton>
          </Box>

          {isLoggedIn ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {!isMobile && (
                <Typography
                  sx={{
                    fontSize: '0.95rem',
                    marginRight: 1,
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {userName}
                </Typography>
              )}
              <StyledBadge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                variant="dot"
              >
                <Avatar
                  onClick={handleAvatarClick}
                  onMouseEnter={handleAvatarMouseEnter}
                  sx={{
                    bgcolor: stringToColor(fullName),
                    cursor: 'pointer',
                    width: 40,
                    height: 40,
                    fontSize: '0.875rem',
                    '&:hover': {
                      opacity: 0.8,
                      transition: 'opacity 0.2s'
                    }
                  }}
                >
                  {getAvatarInitials(fullName)}
                </Avatar>
              </StyledBadge>

              {/* Dropdown Menu */}
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                
                
              >
                <MenuItem onClick={handleDashboard} sx={{ py:1, px:2 }}>
                  <ListItemIcon>
                    <HomeRoundedIcon fontSize="small" sx={{color:"#016799"}}/>
                  </ListItemIcon>
                  <Typography>Dashboard</Typography>
                </MenuItem>
                
                <MenuItem onClick={handleLogout} sx={{ py:1, px:2 }}>
                  <ListItemIcon>
                    <LogoutRoundedIcon fontSize="small" sx={{color:"#016799"}}/>
                  </ListItemIcon>
                  <Typography>Logout</Typography>
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            /* Login Button for Non-Logged In Users */
            <MUIButton
              onClick={handleLogin}
              variant="contained"
              sx={{
                backgroundColor: '#0c6ccc',
                color: 'white',
                textTransform: 'none',
                fontSize: '0.9rem',
                fontWeight: 550,
                paddingX: '1.2rem',
                paddingY: '0.35rem',
                '&:hover': {
                  backgroundColor: '#005b89'
                }
              }}
            >
              Login
            </MUIButton>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export default Header;
